import type { PopupConfig, PopupInstance, Trigger, DisplayMode } from './types';
import { createTrigger } from './triggers';
import { createDisplayMode } from './display-modes';
import { isDismissed, setDismissed } from './cookies';

export class Popup implements PopupInstance {
	config: PopupConfig;
	private trigger: Trigger | null = null;
	private displayMode: DisplayMode;
	private container: HTMLElement | null = null;
	private iframe: HTMLIFrameElement | null = null;
	private visible = false;
	private iframeReady = false;
	private messageHandler: ((e: MessageEvent) => void) | null = null;
	private expectedOrigin: string;

	constructor(config: PopupConfig) {
		this.config = config;
		this.displayMode = createDisplayMode(config.display.displayMode);

		try {
			const url = new URL(config.iframeUrl);
			this.expectedOrigin = url.origin;
		} catch {
			this.expectedOrigin = window.location.origin;
		}
	}

	init(): void {
		if (this.config.behavior.respectDismissal && isDismissed(this.config.behavior.cookieName)) {
			return;
		}

		this.container = this.displayMode.createContainer(this);
		document.body.appendChild(this.container);

		this.createIframe();
		this.setupMessageListener();

		if (this.config.trigger.type !== 'js_api') {
			this.trigger = createTrigger(this.config.trigger.type);
			this.trigger?.init(this, this.config.trigger);
		}

		if (this.config.display.closeOnEsc) {
			document.addEventListener('keydown', this.handleEsc);
		}
	}

	show(): void {
		if (this.visible || !this.container) return;

		if (this.config.behavior.respectDismissal && isDismissed(this.config.behavior.cookieName)) {
			return;
		}

		this.visible = true;
		this.displayMode.show(this.container, this.config.display);

		if (this.iframe && !this.iframeReady) {
			this.iframe.src = this.config.iframeUrl;
		}
	}

	hide(): void {
		if (!this.visible || !this.container) return;

		this.visible = false;
		this.displayMode.hide(this.container, this.config.display);

		setDismissed(this.config.behavior.cookieName, this.config.behavior.cookieDuration);
	}

	isVisible(): boolean {
		return this.visible;
	}

	destroy(): void {
		this.trigger?.destroy();
		if (this.container) {
			this.displayMode.destroy(this.container);
		}
		if (this.messageHandler) {
			window.removeEventListener('message', this.messageHandler);
		}
		document.removeEventListener('keydown', this.handleEsc);
		this.container = null;
		this.iframe = null;
	}

	private createIframe(): void {
		this.iframe = document.createElement('iframe');
		this.iframe.className = 'gf-popups-iframe';
		this.iframe.setAttribute('frameborder', '0');
		this.iframe.setAttribute('title', 'Popup Form');
		this.iframe.style.width = '100%';
		this.iframe.style.height = '400px';
		this.iframe.style.border = 'none';

		if (this.config.behavior.preloadIframe) {
			this.iframe.src = this.config.iframeUrl;
		}

		const content = this.container?.querySelector('.gf-popups-content');
		content?.appendChild(this.iframe);
	}

	private setupMessageListener(): void {
		this.messageHandler = (e: MessageEvent) => {
			if (e.origin !== this.expectedOrigin) return;

			const data = e.data;
			if (!data || typeof data !== 'object') return;
			if (data.feedId !== this.config.feedId) return;

			switch (data.type) {
				case 'gf_popups_ready':
					this.iframeReady = true;
					break;

				case 'gf_popups_resize':
					if (this.iframe && typeof data.height === 'number') {
						this.iframe.style.height = data.height + 'px';
					}
					break;

				case 'gf_popups_form_submitted':
					if (this.config.behavior.autoCloseOnSubmission) {
						setTimeout(() => this.hide(), 1500);
					}
					break;

			case 'gf_popups_redirect':
				if (data.url && typeof data.url === 'string') {
					try {
						const redirectUrl = new URL(data.url, window.location.origin);
						if (redirectUrl.origin === window.location.origin) {
							this.hide();
							window.location.href = redirectUrl.href;
						}
					} catch { /* invalid URL, ignore */ }
				}
				break;

				case 'gf_popups_dismiss':
					this.hide();
					break;
			}
		};

		window.addEventListener('message', this.messageHandler);
	}

	private handleEsc = (e: KeyboardEvent): void => {
		if (e.key === 'Escape' && this.visible) {
			this.hide();
		}
	};
}
